// src/components/layout/NotificationBell.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './NotificationBell.module.css';
import { notificationsApi } from '../../api';

const POLL_INTERVAL_MS = 60000;

const TYPE_ICONS = {
  NEW_BAND: '📚',
  DATE_CHANGED: '📅',
  RELEASED: '🎉',
};

function formatTimeAgo(isoDate, t) {
  if (!isoDate) return '';
  const diffMs = Date.now() - new Date(isoDate).getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return t('notifications.justNow');
  if (minutes < 60) return t('notifications.minutesAgo', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('notifications.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  return t('notifications.daysAgo', { count: days });
}

function NotificationBell() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    const result = await notificationsApi.getNotifications({ cursor: 0, limit: 50 });
    if (result.success) {
      const loadedItems = result.data?.items || [];
      setItems(loadedItems);
      setUnreadCount(loadedItems.filter((n) => !n.read).length);
    }
    setLoading(false);
  }, []);

  // Initial laden + periodisch pollen (Popup-Benachrichtigungen)
  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  // Klick außerhalb schließt Dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleItemClick = async (item) => {
    if (!item.read) {
      setItems((prev) => prev.map((n) => (n.id === item.id ? { ...n, read: true } : n)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
      notificationsApi.markRead(item.id).catch(() => {});
    }
    setOpen(false);
    navigate('/mein-regal');
  };

  const handleMarkAllRead = async (e) => {
    e.stopPropagation();
    if (unreadCount === 0) return;
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
    await notificationsApi.markAllRead();
  };

  return (
    <div className={styles.bellWrapper} ref={dropdownRef}>
      <button
        className={styles.bellButton}
        onClick={() => setOpen((prev) => !prev)}
        title={t('notifications.title')}
        aria-label={t('notifications.title')}
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M10 2.5C7.5 2.5 5.5 4.5 5.5 7v3.5L4 13.5h12L14.5 10.5V7c0-2.5-2-4.5-4.5-4.5z"
            stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"
          />
          <path d="M8 16a2 2 0 004 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        {unreadCount > 0 && (
          <span className={styles.badge}>{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>

      {open && (
        <div className={styles.dropdown}>
          <div className={styles.dropdownHeader}>
            <span>{t('notifications.title')}</span>
            {unreadCount > 0 && (
              <button className={styles.markAllBtn} onClick={handleMarkAllRead}>
                {t('notifications.markAllRead')}
              </button>
            )}
          </div>

          <div className={styles.dropdownList}>
            {loading && items.length === 0 ? (
              <div className={styles.emptyState}>{t('notifications.loading')}</div>
            ) : items.length === 0 ? (
              <div className={styles.emptyState}>{t('notifications.empty')}</div>
            ) : (
              items.map((item) => (
                <div
                  key={item.id}
                  className={`${styles.notificationItem} ${!item.read ? styles.unread : ''}`}
                  onClick={() => handleItemClick(item)}
                >
                  <span className={styles.itemIcon}>{TYPE_ICONS[item.type] || '🔔'}</span>
                  <div className={styles.itemContent}>
                    <span className={styles.itemTitle}>{item.title}</span>
                    {item.body && <span className={styles.itemBody}>{item.body}</span>}
                    <span className={styles.itemTime}>{formatTimeAgo(item.created_at, t)}</span>
                  </div>
                  {!item.read && <span className={styles.unreadDot} />}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
